import type { MarketingTranslationKeys } from "../../types.js";

export const marketing: MarketingTranslationKeys = {
  meta: {
    defaultTitle: "HomeExpense — Claridad financiera para tu hogar",
    defaultDescription:
      "Gestiona gastos del hogar con prorrateo inteligente, escaneo con IA, presupuesto y bóveda de garantías.",
    featuresTitle: "Funcionalidades — HomeExpense",
    featuresDescription:
      "Prorrateo, escaneo OCR con IA, diario de gastos, presupuesto y bóveda de garantías para tu hogar.",
    methodologyTitle: "Metodología — HomeExpense",
    methodologyDescription:
      "Cómo pensamos la experiencia: precisión, estabilidad a largo plazo y una interfaz que prioriza el foco.",
    legalTitle: "Legal y contacto — HomeExpense",
    legalDescription:
      "Privacidad, términos, seguridad y cómo contactarnos. Transparencia en el tratamiento de tus datos.",
    privacyTitle: "Política de privacidad — HomeExpense",
    privacyDescription:
      "Información sobre el responsable del tratamiento, datos recogidos, finalidades, conservación, terceros y tus derechos.",
    termsTitle: "Términos de uso — HomeExpense",
    termsDescription:
      "Condiciones generales de uso del sitio web y las aplicaciones HomeExpense: cuenta, uso aceptable, propiedad intelectual y limitaciones.",
    pricingTitle: "Precios — HomeExpense",
    pricingDescription:
      "Planes Hogar, Smart scan e Insights: funciones, límites de IA y precios de referencia pensados para familias y convivencias.",
  },
  nav: {
    ariaMain: "Navegación principal",
    brand: "HomeExpense",
    home: "Inicio",
    features: "Funcionalidades",
    methodology: "Metodología",
    pricing: "Precios",
    legal: "Legal y contacto",
    register: "Crear cuenta",
    login: "Iniciar sesión",
    openMenu: "Abrir menú",
    closeMenu: "Cerrar menú",
  },
  footer: {
    tagline: "Gestión clara de gastos del hogar.",
    platformTitle: "Producto",
    legalTitle: "Legal",
    supportTitle: "Soporte",
    privacy: "Privacidad",
    terms: "Términos",
    security: "Seguridad",
    contact: "Contacto",
    copyright: "© {{year}} HomeExpense. Todos los derechos reservados.",
  },
  images: {
    landingHeroAlt: "Vista de panel financiero minimalista en una tablet, tonos neutros y acentos azules.",
    landingVizAlt: "Visualización abstracta de datos financieros en tonos azul marino, verde y oro.",
    featuresHeroAlt: "Escritorio con material de finanzas, luz natural y líneas limpias.",
    featuresProrateAlt: "Panel con gráficos y métricas de reparto de gastos en un tablet.",
    featuresAiAlt: "Teléfono escaneando un ticket con líneas digitales sobre el texto.",
    legalMapAlt: "Mapa estilizado en tonos grises de una ciudad.",
    methodologyHeroAlt: "Detalle arquitectónico minimalista, líneas limpias y sombras suaves.",
    methodologyOfficeAlt: "Oficina moderna con luz natural, mobiliario minimalista.",
  },
  home: {
    heroEyebrow: "Gestión de hogar",
    heroTitleLine1: "Claridad financiera",
    heroTitleLine2: "para tu hogar",
    heroDescription:
      "Prorrateo inteligente, diario de gastos, presupuesto, escaneo con IA y bóveda de garantías. Una herramienta pensada para familias y convivencias.",
    ctaPrimary: "Crear cuenta gratis",
    ctaSecondary: "Ver metodología",
    cardSurplusLabel: "Ejemplo de ahorro mensual",
    cardSurplusValue: "1.240,00 €",
    highlightsTitle: "Por qué HomeExpense",
    highlight1Label: "Enfoque",
    highlight1Value: "Hogar compartido",
    highlight2Label: "Precios",
    highlight2Value: "Plan gratuito",
    highlight3Label: "Producto",
    highlight3Value: "Mejoras continuas",
    highlight4Label: "Privacidad",
    highlight4Value: "Datos bajo tu control",
    featuresEyebrow: "Lo esencial de la app",
    featuresTitle: "Funciones alineadas con tu día a día",
    featuresSubtitle:
      "Las mismas áreas que verás en el panel: gastos, presupuesto, escaneo y bóveda, con reparto justo entre miembros.",
    featureProrateTitle: "Prorrateo inteligente",
    featureProrateDesc:
      "Reparte gastos recurrentes y puntuales según miembros, porcentajes y reglas del hogar.",
    featureOcrTitle: "IA y escaneo",
    featureOcrDesc:
      "Sube tickets y deja que el sistema extraiga importe, comercio y categoría para revisar y registrar.",
    featureVaultTitle: "Bóveda de garantías",
    featureVaultDesc:
      "Activo doméstico con fechas de compra y garantía; avisos y documentación ordenada.",
    visualizeTitle: "Visibilidad del presupuesto",
    visualizeSubtitle:
      "Barras de progreso y vistas mensuales para ver reservado, gastado y objetivos por categoría.",
    bar1Label: "Impuestos trimestrales (ejemplo)",
    bar1Value: "65 % reservado",
    bar2Label: "Fondo reforma (ejemplo)",
    bar2Value: "42 % del objetivo",
    ctaBottomTitle: "¿Empezamos?",
    ctaBottomSubtitle:
      "Crea tu cuenta en segundos y entra al panel para configurar tu hogar.",
    ctaBottomButton: "Registrarme gratis",
  },
  featuresPage: {
    heroEyebrow: "Producto",
    heroTitle: "Herramientas para el hogar",
    heroTitleItalic: "de hoy",
    heroDescription:
      "Más que un listado: diario con filtros, presupuesto con prorrateo, escaneo con IA y bóveda para garantías y documentos.",
    f01Label: "Función 01",
    f01Title: "Prorrateo inteligente",
    f01Body:
      "Alinea gastos con fechas de alta, cambios de miembros y reglas del hogar para un reparto coherente.",
    f01aTitle: "Lógica por fechas",
    f01aBody: "Útil cuando alguien se une a mitad de mes o cambia su aportación.",
    f01bTitle: "Reparto ponderado",
    f01bBody: "Porcentajes por miembro según lo acordado en la casa.",
    profileLabel: "Perfil de reparto (ejemplo)",
    demoRowALabel: "Zona A",
    demoRowAPct: "42 %",
    demoRowBLabel: "Zona B",
    demoRowBPct: "28 %",
    demoRowCLabel: "Zona común",
    demoRowCPct: "30 %",
    f02Label: "Función 02",
    f02Title: "IA y OCR",
    f02Body:
      "Convierte tickets en datos estructurados: importe, fecha, comercio y categoría sugerida para que solo confirmes.",
    f02Quote:
      "El flujo está pensado para que revises y guardes en pocos toques tras el escaneo.",
    f02QuoteAttribution: "— Equipo HomeExpense",
    overlayComplete: "Análisis listo",
    overlayCategoryLine: "Categoría sugerida • importe detectado",
    f03Eyebrow: "Función 03",
    f03Title: "Bóveda de garantías",
    f03Subtitle:
      "Registra electrodomésticos y activos con fechas, documentos y avisos antes de que venza la cobertura.",
    vaultB1Title: "Avisos de caducidad",
    vaultB1Body:
      "Recuerda revisiones y garantías con antelación para actuar a tiempo.",
    vaultChip1: "Notificaciones",
    vaultChip2: "Resúmenes por email",
    vaultB2Title: "Documentación para reclamaciones",
    vaultB2Body: "Mantén pruebas y datos del activo accesibles cuando los necesites.",
    vaultB3Title: "Activos identificables",
    vaultB3Body: "Referencia clara por nombre, tipo y fechas clave.",
    vaultB4Title: "Historial asociado",
    vaultB4Body: "Vincula notas o facturas de servicio al mismo activo.",
    ctaTitle: "Empieza hoy",
    ctaSubtitle: "Crea tu cuenta y configura tu hogar en minutos.",
    ctaPrimary: "Crear cuenta",
    ctaSecondary: "Ver metodología",
  },
  methodologyPage: {
    heroEyebrow: "Nuestra metodología",
    heroTitle: "HomeExpense:",
    heroTitleAccent: "claridad con intención",
    heroDescription:
      "Creemos que gestionar un hogar es ordenar decisiones en el tiempo. Priorizamos datos fiables, flujos cortos y una interfaz que no compita con tus números.",
    precisionTitle: "Precisión",
    precisionBody:
      "Evitamos atajos que distorsionen totales: categorías, reparto y mes fiscal del hogar se respetan en pantalla y en la API.",
    accuracyLabel: "Enfoque",
    accuracyValue: "Datos consistentes",
    stabilityTitle: "Estabilidad",
    stabilityBody:
      "Presupuesto y diario miran al mes y al futuro cercano: recurrentes, prorrateo y saldo de conjunto.",
    growthTitle: "Visión de conjunto",
    growthSubtitle: "Resúmenes que ayudan a decidir el siguiente paso.",
    riskTitle: "Menos sorpresas",
    riskSubtitle: "Garantías y recordatorios para obligaciones y activos.",
    narrativeEyebrow: "Experiencia",
    narrativeQuote:
      "Un hogar es un sistema: gastos, personas y tiempo. La app es el lugar donde ese sistema se ve entero.",
    narrativeManifesto: "Manifiesto del producto",
    pillar1Num: "01",
    pillar1Title: "Interfaz sobria",
    pillar1Body:
      "Jerarquía clara para que el dato importante lleve el peso visual.",
    pillar2Num: "02",
    pillar2Title: "Historias con datos",
    pillar2Body:
      "Listados, totales y barras que explican qué pasó y qué sigue.",
    pillar3Num: "03",
    pillar3Title: "Herramientas de hogar",
    pillar3Body:
      "Casas, miembros, presupuesto, bóveda y escaneo en un mismo flujo.",
    breakTitle: "Pensado para quien organiza el hogar.",
    breakCta: "Ver funcionalidades",
  },
  legalPage: {
    heroEyebrow: "Compromiso",
    heroTitle: "Privacidad, seguridad y",
    heroTitleAccent: "contacto directo",
    heroDescription:
      "Aquí encontrarás enlaces a la política de privacidad y a los términos de uso, información de seguridad y cómo contactarnos.",
    controllerName: "GProjectdevelop LLC (Estados Unidos).",
    controllerTaxNote:
      "La sociedad se encuentra en proceso de constitución; los identificadores fiscales definitivos se publicarán en esta página cuando estén disponibles.",
    controllerPresence: "Equipo distribuido con presencia en Miami (EE. UU.) y Asturias (España), sin domicilio comercial presencial único.",
    privacyTitle: "Privacidad",
    privacyLead:
      "No vendemos tus datos financieros. El cifrado en tránsito y buenas prácticas de almacenamiento son la base del servicio.",
    privacyBullet1: "Comunicaciones protegidas con HTTPS.",
    privacyBullet2: "Acceso a tus espacios solo con tu cuenta autenticada.",
    privacyBullet3: "Derecho a solicitar borrado de datos según normativa aplicable.",
    privacyReadMore: "Ver política de privacidad completa",
    termsSectionLabel: "Acuerdo",
    termsTitle: "Términos de uso",
    termsBody:
      "Las condiciones regulan el acceso y uso del sitio web y de las aplicaciones HomeExpense. Te recomendamos leer el documento completo.",
    termsUpdatedLabel: "Última actualización",
    termsUpdatedValue: "10 de mayo de 2026",
    termsCta: "Ver términos de uso",
    securityTitle: "Seguridad",
    securityBody:
      "Infraestructura y prácticas alineadas con estándares habituales en SaaS; reforzamos donde la API y el auth lo exigen.",
    securityChip1: "HTTPS",
    securityChip2: "Autenticación segura",
    contactTitle: "Contacto",
    contactLead:
      "Para soporte, privacidad o consultas de datos escríbenos por correo o usa el formulario; las consultas relativas a protección de datos pueden dirigirse al mismo canal.",
    contactMapTitle: "Presencia",
    contactMapBody:
      "Equipo distribuido entre Miami (Florida, EE. UU.) y Asturias (España). No mantenemos una oficina de atención al público en una dirección única.",
    formName: "Nombre",
    formNamePlaceholder: "Tu nombre",
    formEmail: "Correo",
    formEmailPlaceholder: "tu@email.com",
    formMessage: "Mensaje",
    formMessagePlaceholder: "¿En qué podemos ayudarte?",
    formSubmit: "Enviar",
    formSubmitting: "Enviando…",
    formSuccess: "Mensaje enviado. Te responderemos pronto.",
    formErrorGeneric: "No se pudo enviar el mensaje. Inténtalo de nuevo más tarde.",
    formErrorValidation: "Revisa nombre, correo y mensaje (mínimo 10 caracteres).",
    formErrorConfig: "El envío de formularios no está configurado en este entorno.",
  },
  privacyPage: {
    documentEyebrow: "Transparencia",
    documentTitle: "Política de privacidad",
    lastUpdatedLabel: "Última actualización",
    lastUpdatedValue: "10 de mayo de 2026",
    intro:
      "En HomeExpense nos tomamos en serio la privacidad. Esta política describe, de forma general, qué datos personales podemos tratar cuando utilizas nuestro sitio web o aplicaciones móviles, con qué finalidades y qué derechos tienes. El servicio puede evolucionar; si eres residente en el Espacio Económico Europeo u otras regiones con normativa específica, algunos extremos pueden requerir precisiones adicionales que podrás solicitar por los canales indicados al final.",
    sectionControllerTitle: "1. Responsable del tratamiento",
    sectionControllerBody:
      "El responsable del tratamiento de los datos personales obtenidos a través del producto HomeExpense, en la relación con usuarios y visitantes de los canales oficiales, es GProjectdevelop LLC, con domicilio en Estados Unidos, en adelante «nosotros» o «el responsable». La sociedad puede encontrarse en proceso de constitución; los datos de registro o identificadores fiscales definitivos se actualizarán en esta política cuando proceda. Para cualquier cuestión relacionada con esta política o el ejercicio de derechos, puedes escribir a la dirección de correo indicada en el apartado de contacto (incluidas consultas de delegación de protección de datos, si aplica).",
    sectionDataTitle: "2. Datos personales que podemos recoger",
    sectionDataIntro:
      "Según cómo interactúes con el servicio, podemos tratar categorías tales como las siguientes (no todas aplicarán en cada caso):",
    sectionDataBullet1:
      "Datos de cuenta y perfil: identificador, nombre o alias, correo electrónico, preferencias de idioma o zona horaria y credenciales de acceso (gestionadas con medidas de seguridad adecuadas).",
    sectionDataBullet2:
      "Datos de uso del hogar y finanzas personales introducidos por ti u otros miembros autorizados: gastos, presupuestos, categorías, documentos o metadatos asociados a la bóveda, e información que decidas almacenar en la aplicación.",
    sectionDataBullet3:
      "Datos técnicos y de la aplicación: tipo de dispositivo, sistema operativo, versión de la app, identificadores técnicos o de sesión, registros de errores y, en su caso, datos agregados de uso para mejorar la estabilidad del servicio.",
    sectionDataBullet4:
      "Datos de comunicaciones que nos envíes voluntariamente (por ejemplo, mensajes de soporte) y, si procede, metadatos necesarios para gestionar la conversación.",
    sectionDataBullet5:
      "Imágenes o archivos que subas para funciones como el escaneo de tickets; pueden incluir datos visibles en el documento. Te recomendamos no incluir datos sensibles innecesarios.",
    sectionPurposeTitle: "3. Finalidades",
    sectionPurposeBody:
      "Tratamos los datos para proporcionar y mantener el servicio HomeExpense (gestión de hogares, gastos, presupuesto, bóveda y funciones asociadas), autenticar usuarios, personalizar la experiencia dentro de la cuenta, prestar soporte, cumplir obligaciones legales aplicables, mejorar la seguridad y el rendimiento, y comunicarnos contigo en relación con el servicio cuando sea necesario. Si en el futuro incorporáramos comunicaciones comerciales opcionales, solicitaríamos, en su caso, el consentimiento o te ofreceríamos un mecanismo claro de oposición, según la normativa aplicable.",
    sectionLegalBasisTitle: "4. Base legal (marco general)",
    sectionLegalBasisBody:
      "Según el tipo de tratamiento y la normativa que resulte aplicable (por ejemplo el Reglamento (UE) 2016/679, «GDPR»), las bases legales pueden incluir: la ejecución del contrato o condiciones del servicio cuando el tratamiento es necesario para prestarte la funcionalidad solicitada; el interés legítimo del responsable para garantizar la seguridad, la mejora técnica y la prevención del fraude, siempre ponderado frente a tus derechos; el cumplimiento de obligaciones legales; y el consentimiento cuando resulte requerido para finalidades concretas (p. ej. ciertas cookies no esenciales o comunicaciones opcionales). Si necesitas detalle sobre un tratamiento específico, puedes solicitarlo por los medios de contacto indicados.",
    sectionRetentionTitle: "5. Conservación",
    sectionRetentionBody:
      "Conservamos los datos durante el tiempo necesario para cumplir las finalidades descritas y las obligaciones legales. Los criterios incluyen la vigencia de tu cuenta, la necesidad de resolver incidencias o reclamaciones, y los plazos legales de prescripción o auditoría. Cuando los datos ya no sean necesarios, procederemos a su supresión o anonimización conforme a procedimientos internos y a la normativa aplicable.",
    sectionThirdPartiesTitle: "6. Encargados del tratamiento y servicios de terceros",
    sectionThirdPartiesBody:
      "Podemos recurrir a proveedores de infraestructura, alojamiento, correo transaccional, monitorización de errores u otras herramientas que actúen como encargados del tratamiento en nuestro nombre, con los contratos y medidas de seguridad adecuados. También podemos estar obligados a revelar datos a autoridades si la ley lo exige. No vendemos tus datos personales a terceros. Si transferimos datos fuera del Espacio Económico Europeo, aplicaremos salvaguardas reconocidas (por ejemplo cláusulas contractuales tipo) cuando proceda.",
    sectionRightsTitle: "7. Tus derechos",
    sectionRightsIntro:
      "En la medida en que te asistan frente al responsable, puedes ejercer los siguientes derechos (salvo excepciones legales):",
    sectionRightsBullet1: "Acceso a tus datos personales y a información sobre el tratamiento.",
    sectionRightsBullet2: "Rectificación de datos inexactos o incompletos.",
    sectionRightsBullet3: "Supresión («derecho al olvido») cuando proceda.",
    sectionRightsBullet4: "Limitación del tratamiento, oposición y, en su caso, portabilidad de los datos que hayas proporcionado.",
    sectionRightsBullet5:
      "Retirar el consentimiento cuando el tratamiento se base en él, sin afectar a la licitud previa. También puedes presentar una reclamación ante la autoridad de control de protección de datos que corresponda en tu país.",
    sectionMinorsTitle: "8. Menores de edad",
    sectionMinorsBody:
      "HomeExpense no está dirigido a menores de dieciséis (16) años. Si eres padre, madre o tutor y crees que un menor nos ha facilitado datos personales sin tu consentimiento, contáctanos para que podamos adoptar las medidas oportunas.",
    sectionChangesTitle: "9. Cambios en esta política",
    sectionChangesBody:
      "Podemos actualizar esta política para reflejar cambios legales, técnicos o del producto. Publicaremos la versión vigente en esta página y, cuando el cambio sea relevante, procuraremos avisarte por medios razonables (por ejemplo un aviso en la aplicación o por correo). Te recomendamos revisar la política periódicamente.",
    sectionContactTitle: "10. Contacto",
    sectionContactBody:
      "Para ejercer tus derechos, plantear consultas sobre privacidad o solicitar información adicional sobre el tratamiento de tus datos, escríbenos a:",
    contactEmail: "contacto@gprojectdevelop.com",
  },
  termsPage: {
    documentEyebrow: "Condiciones",
    documentTitle: "Términos de uso",
    lastUpdatedLabel: "Última actualización",
    lastUpdatedValue: "10 de mayo de 2026",
    intro:
      "Estos términos regulan el acceso y uso del sitio web en home-expense.gprojectdevelop.com, las aplicaciones móviles HomeExpense y los servicios asociados ofrecidos por GProjectdevelop LLC («nosotros», «el prestador»). Al crear una cuenta, descargar la app o utilizar el servicio, confirmas que has leído y aceptas estas condiciones. Si no estás de acuerdo, no utilices el servicio. Es una primera versión orientativa; conviene revisión jurídica según tu jurisdicción y modelo de negocio.",
    sectionPartyTitle: "1. Partes y estado",
    sectionPartyBody:
      "El servicio es prestado por GProjectdevelop LLC, con domicilio en Estados Unidos. La entidad puede estar en proceso de constitución; la información societaria y fiscal definitiva se publicará en estas páginas cuando esté disponible. «Usuario» eres tú, persona física o la entidad en nombre de la que actúas, que utiliza el servicio.",
    sectionServiceTitle: "2. Descripción del servicio",
    sectionServiceBody:
      "HomeExpense ofrece herramientas digitales para organizar gastos del hogar, presupuestos, miembros, bóveda de documentos y funciones auxiliares (incluidas, según el plan, capacidades con IA). El servicio se ofrece «tal cual» y puede cambiar: nuevas funciones, límites, mantenimiento o interrupciones temporales. No somos asesores financieros, fiscales ni legales; los cálculos y sugerencias son informativos y no sustituyen el criterio profesional.",
    sectionAccountTitle: "3. Cuenta, edad y seguridad",
    sectionAccountBody:
      "Debes proporcionar datos de registro veraces y mantener la confidencialidad de tus credenciales. Eres responsable de la actividad que ocurra en tu cuenta salvo que demuestres un acceso no autorizado notificado sin demora. El servicio no está dirigido a menores de dieciséis (16) años; si eres menor, no debes registrarte. Podemos rechazar el registro, suspender o cerrar cuentas que incumplan estos términos o la ley.",
    sectionAcceptableUseTitle: "4. Uso aceptable",
    sectionAcceptableUseIntro: "Te comprometes a no utilizar el servicio para:",
    sectionAcceptableUseBullet1:
      "Violar leyes aplicables, infringir derechos de terceros o acceder a sistemas o datos ajenos sin autorización.",
    sectionAcceptableUseBullet2:
      "Introducir malware, realizar ingeniería inversa desproporcionada, sondear vulnerabilidades de forma no autorizada o sobrecargar la infraestructura.",
    sectionAcceptableUseBullet3:
      "Revender, sublicenciar o explotar el servicio de forma que compita con el producto sin acuerdo escrito.",
    sectionAcceptableUseBullet4:
      "Subir contenido ilícito, ofensivo o que contenga datos personales de terceros sin base legal adecuada.",
    sectionContentTitle: "5. Tus datos y contenidos",
    sectionContentBody:
      "Conservas la titularidad de la información que introduces (gastos, documentos, etc.). Nos concedes una licencia limitada, no exclusiva y mundial para alojar, procesar, cifrar en tránsito y mostrar esos datos con la finalidad de prestarte el servicio, incluido el tratamiento necesario para funciones de IA si las activas. El tratamiento de datos personales se describe en la política de privacidad. Eres responsable de la licitud de los datos que cargas.",
    sectionFeesTitle: "6. Planes, precios y facturación",
    sectionFeesBody:
      "Algunas funciones pueden ser gratuitas y otras de pago según el plan vigente publicado en el sitio o en la aplicación. Los precios pueden modificarse con aviso razonable cuando la ley lo permita. Impuestos y cargos de terceros (operadores, tiendas de aplicaciones) pueden aplicarse. Si integras pagos en el futuro, regirán las condiciones adicionales que se te muestren al contratar.",
    sectionIpTitle: "7. Propiedad intelectual",
    sectionIpBody:
      "El software, la marca, el diseño y los materiales del servicio (excluido tu contenido) son propiedad del prestador o sus licenciantes. No adquieres derechos sobre ellos salvo los imprescindibles para usar el servicio conforme a estos términos.",
    sectionDisclaimerTitle: "8. Exclusión de garantías",
    sectionDisclaimerBody:
      "En la medida permitida por la ley aplicable, el servicio se presta sin garantías expresas o implícitas de comerciabilidad, idoneidad para un fin concreto o no infracción. No garantizamos resultados económicos ni la ausencia de errores en extracciones automáticas (OCR/IA).",
    sectionLiabilityTitle: "9. Limitación de responsabilidad",
    sectionLiabilityBody:
      "En la medida máxima permitida por la ley, no seremos responsables por daños indirectos, lucro cesante, pérdida de datos o daños consecuentes. Nuestra responsabilidad agregada por reclamaciones relacionadas con el servicio en un período de doce (12) meses no superará, en conjunto, el importe que hayas abonado por el servicio de pago en ese período o, si no hubo pago, cincuenta euros (50 €) o el equivalente en moneda local. Algunas jurisdicciones no permiten ciertas limitaciones; en ese caso aplicará el mínimo legal.",
    sectionIndemnityTitle: "10. Indemnización",
    sectionIndemnityBody:
      "Nos mantendrás indemne frente a reclamaciones de terceros derivadas de tu uso indebido del servicio, de tu contenido o del incumplimiento de estos términos, incluidos honorarios razonables de abogados.",
    sectionTerminationTitle: "11. Baja y suspensión",
    sectionTerminationBody:
      "Puedes dejar de usar el servicio y solicitar el cierre de tu cuenta según los mecanismos disponibles en el producto o por correo. Podemos suspender o terminar el acceso ante incumplimientos graves, riesgo para la seguridad o requerimiento legal. Tras la baja, podemos eliminar datos conforme a la política de privacidad y obligaciones legales.",
    sectionLawTitle: "12. Ley aplicable y resolución de conflictos",
    sectionLawBody:
      "Salvo norma imperativa en tu país de residencia que disponga lo contrario, estos términos se rigen por las leyes de los Estados Unidos de América y, para conflictos derivados del servicio prestado desde Florida, las leyes del Estado de Florida, sin tener en cuenta sus normas sobre conflicto de leyes. Los tribunales competentes serán los que correspondan según dicha elección, sin perjuicio de derechos irrenunciables del consumidor en la Unión Europea u otras regiones.",
    sectionChangesTitle: "13. Cambios",
    sectionChangesBody:
      "Podemos modificar estos términos publicando la nueva versión en esta página y actualizando la fecha de «Última actualización». Si el cambio es sustancial, procuraremos avisarte por medios razonables. El uso continuado tras la entrada en vigor puede implicar la aceptación; si no aceptas, debes cesar el uso del servicio.",
    sectionContactTitle: "14. Contacto",
    sectionContactBody:
      "Para preguntas sobre estos términos o el servicio, escribe a:",
    contactEmail: "contacto@gprojectdevelop.com",
  },
  pricingPage: {
    heroEyebrow: "Planes",
    heroTitle: "Precios claros para tu hogar",
    heroSubtitle:
      "Tres niveles: núcleo sin IA, escaneo con IA con cupo diario, e insights inteligentes con más escaneos. El cobro en línea se activará más adelante.",
    leadTitle: "Transparencia",
    leadBody:
      "Los importes de pago reflejan un coste orientativo de procesamiento con IA más un margen reducido. El plan Hogar sigue siendo gratuito.",
    noPaymentNote: "Aún no activamos el pago en la app: puedes registrarte y usar el producto sin pasarela.",
    priceNote: "Precios de referencia; impuestos aplicables según tu país.",
    plan1Name: "Hogar",
    plan1Tagline: "Todo el núcleo, sin funciones de IA",
    plan1Price: "0 €",
    plan1Period: "/mes",
    plan1RecurringCaption: "Siempre gratuito",
    plan1F1: "Diario de gastos, prorrateo, presupuesto y bóveda de garantías",
    plan1F2: "Miembros del hogar y reglas de reparto personalizables",
    plan1F3: "Sin escaneo de tickets con IA ni sugerencias automáticas",
    plan2Name: "Smart scan",
    plan2Tagline: "Hogar más escaneo con IA",
    plan2Price: "4,99 €",
    plan2Period: "/mes",
    plan2RecurringCaption: "Precio de referencia mensual",
    plan2Popular: "Más equilibrado",
    plan2F1: "Todo lo incluido en Hogar",
    plan2F2: "Hasta 5 escaneos de tickets con IA por día",
    plan2F3: "Extracción de importe, comercio y categoría sugerida",
    plan3Name: "Insights",
    plan3Tagline: "Más IA y sugerencias financieras",
    plan3Price: "12,99 €",
    plan3Period: "/mes",
    plan3RecurringCaption: "Precio de referencia mensual",
    plan3F1: "Todo lo incluido en Smart scan",
    plan3F2: "Hasta 15 escaneos con IA por día",
    plan3F3: "Sugerencias inteligentes sobre patrones y oportunidades en tus gastos",
    cta: "Crear cuenta",
    includesLabel: "Incluye",
  },
};
